import { z } from "zod";
import type { BindingResponse } from "../types";

// =============================================================================
// Plugin Types (Connected Systems)
// =============================================================================

type PluginStatus = "active" | "inactive";
type PluginType = "webhook";
type AuthType = "bearer" | "mtls" | "none";
type HealthStatus = "healthy" | "degraded" | "unhealthy" | "unknown";

type SecretResponse = {
  binding: string;
  editable: boolean;
  exists: boolean;
};

type WebhookAuthResponse = {
  type: AuthType;
  token?: SecretResponse;
  client_cert_configured: boolean;
  client_key_configured: boolean;
  ca_cert?: string;
};

type WebhookEndpointResponse = {
  url: string;
  timeout_ms: number;
};

type WebhookEndpointsResponse = {
  access?: WebhookEndpointResponse;
  pre?: WebhookEndpointResponse;
  post?: WebhookEndpointResponse;
};

type RetryConfigResponse = {
  enabled: boolean;
  max_attempts: number;
  backoff: "fixed" | "exponential";
  base_delay_ms: number;
  retry_on: string[];
};

type WebhookConfigResponse = {
  endpoints: WebhookEndpointsResponse;
  health_check_path?: string;
  auth?: WebhookAuthResponse;
};

export type Plugin = {
  id: string;
  plugin_type: PluginType;
  name: string;
  description?: string;
  status: PluginStatus;
  binding: BindingResponse;
  webhook_config?: WebhookConfigResponse;
  retry?: RetryConfigResponse;
  version: number;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
  health_status: HealthStatus;
};

export type PluginListResponse = {
  items: Plugin[];
  limit: number;
  offset: number;
  page_count: number;
  total_count: number;
};

// =============================================================================
// Hook Types (Active Rules)
// =============================================================================

type HookPointStatus = "active" | "inactive";
export type HookPoint = "tool.access" | "tool.pre" | "tool.post";
export type Phase = "before" | "after";
type FailureMode = "fail_closed" | "fail_open";

export type Hook = {
  id: string;
  name: string;
  plugin_id: string;
  hook_point: HookPoint;
  status: HookPointStatus;
  phase: Phase;
  priority: number;
  failure_mode: FailureMode;
  timeout_ms: number;
  cache_enabled?: boolean;
  cache_ttl_ms?: number;
  version: number;
  created_at: string;
  updated_at: string;
};

export type HookListResponse = {
  items: Hook[];
  limit: number;
  offset: number;
  page_count: number;
  total_count: number;
};

// =============================================================================
// Create/Update Request Types
// =============================================================================

const MAX_NAME_LENGTH = 256;
const MAX_DESCRIPTION_LENGTH = 1000;

// Webhook endpoint configuration for creation
const webhookEndpointSchema = z.object({
  url: z.string().url("Must be a valid URL"),
  status: z.enum(["active", "inactive"]).default("inactive"),
  phase: z.enum(["before", "after"]).default("before"),
  priority: z.number().min(0).max(1000).optional(),
  failure_mode: z.enum(["fail_closed", "fail_open"]).default("fail_closed"),
  timeout_ms: z.number().min(100).max(120_000).optional(), // 100ms to 2 minutes
  cache_enabled: z.boolean().optional(),
  cache_ttl_ms: z.number().min(1000).max(604_800_000).optional(), // Max 1 week in ms
});
// Webhook auth configuration
const webhookAuthSchema = z.object({
  type: z.enum(["bearer", "mtls", "none"]),
  token: z.string().optional(),
  client_cert: z.string().optional(),
  client_key: z.string().optional(),
  ca_cert: z.string().optional(),
});
// Retry configuration
const retryConfigSchema = z.object({
  enabled: z.boolean().default(false),
  max_attempts: z.number().min(1).max(10).default(3),
  backoff: z.enum(["fixed", "exponential"]).default("exponential"),
  base_delay_ms: z.number().min(10).max(10_000).default(100),
  retry_on: z.array(z.enum(["5xx", "timeout", "connection_error"])).default([]),
});
// Create plugin request schema
const createPluginSchema = z.object({
  plugin_type: z.literal("webhook"),
  name: z
    .string()
    .min(1, "Name is required")
    .max(MAX_NAME_LENGTH, "Name is too long"),
  description: z
    .string()
    .max(MAX_DESCRIPTION_LENGTH, "Description is too long")
    .optional(),
  status: z.enum(["active", "inactive"]).optional(),
  binding_type: z.enum(["tenant", "project"]).optional(),
  webhook_config: z.object({
    endpoints: z.object({
      access: webhookEndpointSchema.optional(),
      pre: webhookEndpointSchema.optional(),
      post: webhookEndpointSchema.optional(),
    }),
    health_check_path: z.string().url().optional(),
    auth: webhookAuthSchema.optional(),
  }),
  retry: retryConfigSchema.optional(),
});
export type CreatePluginRequest = z.infer<typeof createPluginSchema>;

// Patch plugin request schema (all fields optional)
const patchPluginSchema = z.object({
  name: z.string().min(1).max(MAX_NAME_LENGTH).optional(),
  description: z.string().max(MAX_DESCRIPTION_LENGTH).optional(),
  status: z.enum(["active", "inactive"]).optional(),
  webhook_config: z
    .object({
      endpoints: z
        .object({
          access: z
            .object({
              url: z.string().url().optional(),
              status: z.enum(["active", "inactive"]).optional(),
              phase: z.enum(["before", "after"]).optional(),
              priority: z.number().min(0).max(1000).optional(),
              failure_mode: z.enum(["fail_closed", "fail_open"]).optional(),
              cache_enabled: z.boolean().optional(),
              cache_ttl_ms: z.number().min(1000).max(604_800_000).optional(), // Max 1 week in ms
            })
            .optional(),
          pre: z
            .object({
              url: z.string().url().optional(),
              status: z.enum(["active", "inactive"]).optional(),
              phase: z.enum(["before", "after"]).optional(),
              priority: z.number().min(0).max(1000).optional(),
              failure_mode: z.enum(["fail_closed", "fail_open"]).optional(),
              cache_enabled: z.boolean().optional(),
              cache_ttl_ms: z.number().min(1000).max(604_800_000).optional(), // Max 1 week in ms
            })
            .optional(),
          post: z
            .object({
              url: z.string().url().optional(),
              status: z.enum(["active", "inactive"]).optional(),
              phase: z.enum(["before", "after"]).optional(),
              priority: z.number().min(0).max(1000).optional(),
              failure_mode: z.enum(["fail_closed", "fail_open"]).optional(),
              cache_enabled: z.boolean().optional(),
              cache_ttl_ms: z.number().min(1000).max(604_800_000).optional(), // Max 1 week in ms
            })
            .optional(),
        })
        .optional(),
      health_check_path: z.string().url().optional(),
      auth: webhookAuthSchema.optional(),
    })
    .optional(),
  retry: retryConfigSchema.partial().optional(),
});
export type PatchPluginRequest = z.infer<typeof patchPluginSchema>;

// =============================================================================
// UI Helper Types
// =============================================================================

export type HookPointInfo = {
  hookPoint: HookPoint;
  displayName: string;
  description: string;
};

export const HOOK_POINT_INFO: Record<HookPoint, HookPointInfo> = {
  "tool.access": {
    hookPoint: "tool.access",
    displayName: "Tool Access",
    description:
      "Defines which users can see a tool. Controls tool visibility in the catalog.",
  },
  "tool.pre": {
    hookPoint: "tool.pre",
    displayName: "Pre tool execution",
    description:
      "Checks that run before a tool executes. Validate inputs, check permissions, enrich data.",
  },
  "tool.post": {
    hookPoint: "tool.post",
    displayName: "Post tool execution",
    description:
      "Checks that run after a tool executes. Redact sensitive data, filter outputs.",
  },
};

// Extended hook with plugin info for UI display
export type HookWithPlugin = Hook & {
  plugin?: Plugin;
};

// =============================================================================
// Bulk Hook Update Types
// =============================================================================

/** Single hook configuration for bulk update */
export type BulkHookConfig = {
  plugin_id: string;
  hook_point: HookPoint;
  status?: HookPointStatus;
  phase?: Phase;
  priority?: number;
  failure_mode?: FailureMode;
};

/** Request payload for bulk upsert hooks */
export type BulkUpsertHooksRequest = {
  configs: BulkHookConfig[];
};

/** Result of a single hook in bulk upsert */
type BulkHookResult = {
  plugin_id: string;
  hook_point: string;
  hook_id: string;
  action: "created" | "updated" | "unchanged";
  error?: string;
};

/** Response from bulk upsert hooks */
export type BulkUpsertHooksResponse = {
  results: BulkHookResult[];
  success: number;
  failed: number;
};
